import { Container, Stack, Title, Text, Group } from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import { useState } from 'react';
import { subDays } from 'date-fns';
import { usePowerData } from '@/hooks/usePowerData';
import { TotalPowerChart } from '@/features/power/TotalPowerChart';
import { OutletPowerChart } from '@/features/power/OutletPowerChart';
import { VoltageChart } from '@/features/power/VoltageChart';
import { OutletRuntimeSection } from '@/features/power/OutletRuntimeSection';

export default function PowerPage() {
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    subDays(new Date(), 7),
    new Date(),
  ]);

  const { data: powerData, isLoading, error } = usePowerData({
    startDate: dateRange[0],
    endDate: dateRange[1],
  });

  const voltageChannels = powerData?.channels.filter(c => c.reading_type === 'volts') ?? [];

  const handleDateChange = (dates: DatesRangeValue): void => {
    if (!dates) return;
    const [start, end] = dates;
    if (start && end) {
      const startDate = start instanceof Date ? start : new Date(start);
      const endDate = end instanceof Date ? end : new Date(end);
      setDateRange([startDate, endDate]);
    }
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={2}>Power Monitoring</Title>
            <Text c="dimmed" size="sm">EnergyBar power consumption and voltage</Text>
          </div>
          <DatePickerInput
            type="range"
            label="Date range"
            placeholder="Pick dates range"
            defaultValue={dateRange}
            onChange={handleDateChange}
            maxDate={new Date()}
            clearable={false}
            valueFormat="MMM DD, YYYY"
            allowSingleDateInRange
          />
        </Group>

        <TotalPowerChart
          data={powerData?.data ?? []}
          isLoading={isLoading}
          error={error}
        />

        <OutletPowerChart
          data={powerData?.data ?? []}
          channels={powerData?.channels ?? []}
          isLoading={isLoading}
          error={error}
        />

        {voltageChannels.length > 0 && (
          <VoltageChart
            data={powerData?.data ?? []}
            voltageChannels={voltageChannels}
            isLoading={isLoading}
            error={error}
          />
        )}

        <OutletRuntimeSection dateRange={dateRange} />
      </Stack>
    </Container>
  );
}
